import React, { useState, useEffect } from 'react'
import {
  Box,
  Text,
  Button,
  Stack,
  Group,
  Loader,
  Alert,
  Textarea,
  Divider
} from '@mantine/core'
import { FiArrowLeft, FiHeart, FiShare2, FiTrash2, FiMessageSquare, FiCornerDownRight, FiSend, FiInfo } from 'react-icons/fi'
import { useParams, useHistory } from 'react-router-dom'
import agent from '../../app/api/agent'
import type { SlopPostDto, SlopPostCommentDto } from '../../app/models/slopPost'
import { store } from '../../app/stores/store'

export default function SlopPostPage () {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const isAdmin = store.userStore.user?.isAdmin ?? false
  const isLoggedIn = !!store.userStore.user

  const [post, setPost] = useState<SlopPostDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [comments, setComments] = useState<SlopPostCommentDto[]>([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [commentSubmitting, setCommentSubmitting] = useState(false)
  const [replyingToId, setReplyingToId] = useState<number | null>(null)
  const [replyText, setReplyText] = useState('')
  const [shareCopied, setShareCopied] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    agent.SlopFeed.getDetails(id)
      .then((p) => { setPost(p) })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    if (!post) return
    setCommentsLoading(true)
    agent.SlopFeed.getComments(post.id)
      .then(setComments)
      .catch(() => setComments([]))
      .finally(() => setCommentsLoading(false))
  }, [post?.id])

  const requireAuth = (action: () => void) => {
    if (!isLoggedIn) { history.push('/register'); return }
    action()
  }

  const handleToggleLike = async () => {
    if (!post) return
    const result = await agent.SlopFeed.toggleLike(post.id)
    setPost({ ...post, likeCount: result.newLikeCount, isLikedByCurrentUser: result.isLiked })
  }

  const handleShare = async () => {
    if (!post) return
    const url = `${window.location.origin}/slop-feed/${post.id}`
    if (navigator.share) {
      try { await navigator.share({ title: post.title || 'GunSlop Video', url }) } catch {}
    } else {
      await navigator.clipboard.writeText(url)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    }
  }

  const handleDelete = async () => {
    if (!post) return
    setDeleteLoading(true)
    try {
      await agent.SlopFeed.deletePost(post.id)
      history.push('/slop-feed')
    } catch {
      setDeleteLoading(false)
    }
  }

  const handleAddComment = async () => {
    if (!post || !newComment.trim()) return
    setCommentSubmitting(true)
    try {
      const created = await agent.SlopFeed.addComment(post.id, newComment.trim())
      setComments((prev) => [...prev, created])
      setNewComment('')
      setPost((p) => p ? { ...p, commentCount: p.commentCount + 1 } : p)
    } catch { } finally { setCommentSubmitting(false) }
  }

  const handleAddReply = async (parentId: number) => {
    if (!post || !replyText.trim()) return
    setCommentSubmitting(true)
    try {
      const created = await agent.SlopFeed.addComment(post.id, replyText.trim(), parentId)
      setComments((prev) =>
        prev.map((c) => c.id === parentId ? { ...c, replies: [...c.replies, created] } : c)
      )
      setReplyText(''); setReplyingToId(null)
      setPost((p) => p ? { ...p, commentCount: p.commentCount + 1 } : p)
    } catch { } finally { setCommentSubmitting(false) }
  }

  const handleDeleteComment = async (commentId: number, parentId?: number) => {
    if (!post) return
    try {
      await agent.SlopFeed.deleteComment(post.id, commentId)
      if (parentId != null) {
        setComments((prev) =>
          prev.map((c) => c.id === parentId ? { ...c, replies: c.replies.filter((r) => r.id !== commentId) } : c)
        )
      } else {
        setComments((prev) => prev.filter((c) => c.id !== commentId))
      }
      setPost((p) => p ? { ...p, commentCount: Math.max(0, p.commentCount - 1) } : p)
    } catch { }
  }

  const formatDate = (date: string) => {
    const d = new Date(date)
    const diffMs = Date.now() - d.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return d.toLocaleDateString()
  }

  if (loading) {
    return (
    <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
      <Loader />
    </Box>
    )
  }

  if (notFound || !post) {
    return (
    <Box sx={{ maxWidth: 700, margin: '0 auto', padding: '40px 24px' }}>
      <Text sx={{ color: 'var(--gs-muted)', textAlign: 'center' }}>Video not found.</Text>
      <Group position="center" mt="md">
        <Button variant="subtle" leftIcon={<FiArrowLeft />} onClick={() => history.push('/slop-feed')}>
          Back to Slop Feed
        </Button>
      </Group>
    </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', padding: '24px' }}>
      {/* Back link */}
      <Button
        variant="subtle"
        leftIcon={<FiArrowLeft size={14} />}
        onClick={() => history.push('/slop-feed')}
        sx={{ color: 'var(--gs-muted)', marginBottom: 20, paddingLeft: 0 }}
      >
        Slop Feed
      </Button>

      {/* Video */}
      <Box sx={{ borderRadius: 8, overflow: 'hidden', background: '#000', marginBottom: 20 }}>
        {post.isEmbedded
          ? (
          <Box sx={{ paddingBottom: '56.25%', position: 'relative' }}>
            <iframe
              src={post.embedSourceUrl ?? ''}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              allowFullScreen
            />
          </Box>
            )
          : (
          <video
            controls
            autoPlay={false}
            style={{ width: '100%', display: 'block', maxHeight: 520 }}
          >
            <source src={post.videoUrl ?? ''} />
          </video>
            )}
      </Box>

      {/* Title + meta */}
      <Stack gap="xs" sx={{ marginBottom: 20 }}>
        <Text sx={{ fontSize: 22, fontWeight: 700, fontFamily: 'Oswald, sans-serif', letterSpacing: 1 }}>
          {post.title}
        </Text>
        {post.description && (
          <Text sx={{ fontSize: 14, color: 'var(--gs-muted)' }}>{post.description}</Text>
        )}
        <Group spacing="xs">
          <Text sx={{ fontSize: 13, fontWeight: 600, color: 'var(--gs-accent)' }}>
            {post.username}
          </Text>
          <Text sx={{ fontSize: 12, color: 'var(--gs-muted)' }}>·</Text>
          <Text sx={{ fontSize: 12, color: 'var(--gs-muted)' }}>{formatDate(post.createdAt)}</Text>
        </Group>
      </Stack>

      {/* Action bar */}
      <Group spacing="sm" sx={{ marginBottom: 24 }}>
        <Button
          variant="light"
          leftIcon={<FiHeart size={15} fill={post.isLikedByCurrentUser ? 'currentColor' : 'none'} />}
          onClick={() => requireAuth(handleToggleLike)}
          sx={{ color: post.isLikedByCurrentUser ? 'var(--gs-accent)' : 'inherit' }}
        >
          {post.likeCount} Like{post.likeCount !== 1 ? 's' : ''}
        </Button>

        <Button variant="light" leftIcon={<FiShare2 size={15} />} onClick={handleShare}>
          {shareCopied ? 'Copied!' : 'Share'}
        </Button>

        {(post.userId === store.userStore.user?.id || isAdmin) && (
          <Button
            color="red"
            variant="light"
            leftIcon={<FiTrash2 size={15} />}
            loading={deleteLoading}
            onClick={handleDelete}
          >
            Delete
          </Button>
        )}
      </Group>

      <Divider sx={{ marginBottom: 24 }} />

      {/* Comments */}
      <Stack gap="md">
        <Text sx={{ fontSize: 15, fontWeight: 600 }}>
          <FiMessageSquare style={{ marginRight: 6, verticalAlign: 'middle' }} />
          {post.commentCount} Comment{post.commentCount !== 1 ? 's' : ''}
        </Text>

        {isLoggedIn
          ? (
          <Group align="flex-end" spacing="xs">
            <Box sx={{ flex: 1, minWidth: 500, maxWidth: 500 }}>
              <Textarea
                placeholder="Add a comment…"
                value={newComment}
                onChange={(e) => setNewComment(e.currentTarget.value)}
                autosize minRows={1} maxRows={4}
                disabled={commentSubmitting}
              />
            </Box>
            <Button
              variant="light"
              size="sm"
              leftIcon={<FiSend size={14} />}
              loading={commentSubmitting}
              disabled={!newComment.trim()}
              onClick={handleAddComment}
            >
              Post
            </Button>
          </Group>
            )
          : (
          <Alert icon={<FiInfo size={14} />} sx={{ fontSize: 13 }}>
            <Text
              component="a"
              onClick={() => history.push('/register')}
              sx={{ color: 'var(--gs-accent)', cursor: 'pointer', textDecoration: 'underline' }}
            >Sign up</Text>{' '}or{' '}
            <Text
              component="a"
              onClick={() => history.push('/login')}
              sx={{ color: 'var(--gs-accent)', cursor: 'pointer', textDecoration: 'underline' }}
            >log in</Text>{' '}to comment.
          </Alert>
            )}

        {commentsLoading
          ? (
          <Loader size="sm" style={{ display: 'block', margin: '0 auto' }} />
            )
          : comments.length === 0
            ? (
          <Text sx={{ fontSize: 13, color: 'var(--gs-muted)', textAlign: 'center', padding: '8px 0' }}>
            No comments yet. Be the first!
          </Text>
              )
            : (
          <Stack gap="sm">
            {comments.map((comment) => (
              <Box key={comment.id}>
                <Box sx={{ background: 'var(--gs-surface2)', borderRadius: 6, padding: '10px 12px' }}>
                  <Group position="apart" align="flex-start">
                    <Box sx={{ flex: 1 }}>
                      <Group spacing="xs">
                        <Text sx={{ fontSize: 12, fontWeight: 600, color: 'var(--gs-accent)' }}>
                          {comment.authorUsername}
                        </Text>
                        <Text sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>
                          {formatDate(comment.createdAt)}
                        </Text>
                      </Group>
                      <Text sx={{ fontSize: 13, marginTop: 4, wordBreak: 'break-word' }}>
                        {comment.body}
                      </Text>
                    </Box>
                    <Group spacing={4}>
                      {isLoggedIn && (
                        <Button
                          variant="subtle" size="xs" compact
                          leftIcon={<FiCornerDownRight size={12} />}
                          sx={{ color: 'var(--gs-muted)', fontSize: 11 }}
                          onClick={() => setReplyingToId(replyingToId === comment.id ? null : comment.id)}
                        >Reply</Button>
                      )}
                      {(comment.authorUsername === store.userStore.user?.userName || isAdmin) && (
                        <Button
                          variant="subtle" size="xs" compact
                          leftIcon={<FiTrash2 size={12} />}
                          sx={{ color: 'var(--gs-muted)' }}
                          onClick={() => handleDeleteComment(comment.id)}
                        />
                      )}
                    </Group>
                  </Group>

                  {replyingToId === comment.id && (
                    <Group align="flex-end" spacing="xs" sx={{ marginTop: 8 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Textarea
                          placeholder={`Reply to ${comment.authorUsername}…`}
                          value={replyText}
                          onChange={(e) => setReplyText(e.currentTarget.value)}
                          autosize minRows={1} maxRows={3}
                          disabled={commentSubmitting}
                        />
                      </Box>
                      <Button
                        variant="light" size="xs"
                        leftIcon={<FiSend size={12} />}
                        loading={commentSubmitting}
                        disabled={!replyText.trim()}
                        onClick={() => handleAddReply(comment.id)}
                      >Reply</Button>
                    </Group>
                  )}
                </Box>

                {comment.replies.length > 0 && (
                  <Stack gap={4} sx={{ marginLeft: 24, marginTop: 4 }}>
                    {comment.replies.map((reply) => (
                      <Box
                        key={reply.id}
                        sx={{
                          background: 'var(--gs-surface1)',
                          borderRadius: 6,
                          padding: '8px 12px',
                          borderLeft: '2px solid var(--gs-border)'
                        }}
                      >
                        <Group position="apart" align="flex-start">
                          <Box sx={{ flex: 1 }}>
                            <Group spacing="xs">
                              <Text sx={{ fontSize: 12, fontWeight: 600, color: 'var(--gs-accent)' }}>
                                {reply.authorUsername}
                              </Text>
                              <Text sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>
                                {formatDate(reply.createdAt)}
                              </Text>
                            </Group>
                            <Text sx={{ fontSize: 13, marginTop: 4, wordBreak: 'break-word' }}>
                              {reply.body}
                            </Text>
                          </Box>
                          {(reply.authorUsername === store.userStore.user?.userName || isAdmin) && (
                            <Button
                              variant="subtle" size="xs" compact
                              leftIcon={<FiTrash2 size={12} />}
                              sx={{ color: 'var(--gs-muted)' }}
                              onClick={() => handleDeleteComment(reply.id, comment.id)}
                            />
                          )}
                        </Group>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
              )}
      </Stack>
    </Box>
  )
}
